/**
 * Global localStorage cache for ModelMetadata.
 *
 * Survives WebView reloads so S3 uploads can still attach model name/version
 * while the worker re-downloads weights. Not session-scoped and not subject
 * to the 30-min session TTL.
 */
import type { ModelMetadata } from 'types/models';

const MODEL_METADATA_KEY = 'bmt_model_metadata';

interface CachedModelMetadata {
  metadata: ModelMetadata;
  appVersion: string;
  timestamp: number;
}

const getCurrentAppVersion = (): string =>
  import.meta.env.VITE_VERSION || 'unknown';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const isModelMetadata = (value: unknown): value is ModelMetadata => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const metadata = value as Record<string, unknown>;

  return (
    isNonEmptyString(metadata.localizationModelName) &&
    isNonEmptyString(metadata.localizationModelVersion) &&
    isNonEmptyString(metadata.classificationModelName) &&
    isNonEmptyString(metadata.classificationModelVersion)
  );
};

const isCachedModelMetadata = (value: unknown): value is CachedModelMetadata => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const cached = value as Record<string, unknown>;

  return (
    isModelMetadata(cached.metadata) &&
    isNonEmptyString(cached.appVersion) &&
    typeof cached.timestamp === 'number' &&
    Number.isFinite(cached.timestamp)
  );
};

/** Writes ModelMetadata to localStorage after a successful model load. */
export const persistModelMetadata = (metadata: ModelMetadata): void => {
  try {
    const payload: CachedModelMetadata = {
      metadata,
      appVersion: getCurrentAppVersion(),
      timestamp: Date.now(),
    };
    localStorage.setItem(MODEL_METADATA_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('[modelMetadataCache] Failed to persist model metadata:', error);
  }
};

/**
 * Reads cached ModelMetadata. Returns null when missing, corrupt, or
 * stamped with a different app version than the current build.
 */
export const getPersistedModelMetadata = (): ModelMetadata | null => {
  try {
    const raw = localStorage.getItem(MODEL_METADATA_KEY);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    if (!isCachedModelMetadata(parsed)) {
      return null;
    }

    if (parsed.appVersion !== getCurrentAppVersion()) {
      return null;
    }

    return parsed.metadata;
  } catch (error) {
    console.warn('[modelMetadataCache] Failed to read model metadata:', error);
    return null;
  }
};
